import React, { useState } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import { LanguageSwitcher, useT } from "@/i18n/LanguageContext";
import {
  LayoutGrid, MapPin, Briefcase, ClipboardList, Bell, Shield, UserCircle2, LogOut, Menu, X,
  Building2, TrendingUp, Hammer, Megaphone, Crown, ChevronDown, ChevronRight, Play,
} from "lucide-react";

const NETWORK_NAV = [
  { to: "/dashboard", label: "Dashboard", i18n: "side.dashboard", icon: LayoutGrid, testid: "nav-dashboard" },
  { to: "/tv", label: "Opportunity TV", i18n: "side.tv", icon: Play, testid: "nav-tv" },
  { to: "/opportunities", label: "Opportunities", i18n: "side.opportunities", icon: Briefcase, testid: "nav-opportunities" },
  { to: "/applications", label: "Applications", i18n: "side.applications", icon: ClipboardList, testid: "nav-applications" },
  { to: "/territories", label: "Territories", i18n: "side.territories", icon: MapPin, testid: "nav-territories" },
  { to: "/influencers", label: "Influencers", i18n: "side.influencers", icon: Megaphone, testid: "nav-influencers" },
  { to: "/membership", label: "Membership", i18n: "side.membership", icon: Crown, testid: "nav-membership" },
  { to: "/notifications", label: "Notifications", i18n: "side.notifications", icon: Bell, testid: "nav-notifications" },
];

const PORTAL_NAV = [
  { to: "/portal/business_owner", label: "Business Owner", icon: Building2, role: "business_owner", testid: "nav-portal-business_owner" },
  { to: "/portal/investor", label: "Investor", icon: TrendingUp, role: "investor", testid: "nav-portal-investor" },
  { to: "/portal/contractor", label: "Contractor", icon: Hammer, role: "contractor", testid: "nav-portal-contractor" },
  { to: "/portal/influencer", label: "Influencer", icon: Megaphone, role: "influencer", testid: "nav-portal-influencer" },
];

const SidebarBody = ({ user, isActive, setOpen, handleLogout, portalsOpen, setPortalsOpen }) => {
  const { t } = useT();
  const visiblePortals = user?.is_admin
    ? PORTAL_NAV
    : PORTAL_NAV.filter((p) => p.role === user?.role);

  return (
    <>
      <Link
        to="/dashboard"
        className="flex items-center gap-2 px-6 py-5 border-b border-zinc-200"
        data-testid="sidebar-brand"
      >
        <img src="/brand/miresco-logo.png" alt="MIRESCO" className="w-10 h-10 object-contain" />
        <div className="leading-tight">
          <div className="font-display font-black text-base tracking-tight">MIRESCO</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">BIZ Network</div>
        </div>
      </Link>

      <nav className="flex-1 p-3 space-y-1 overflow-auto">
        {NETWORK_NAV.map((n) => {
          const Icon = n.icon;
          const active = isActive(n.to);
          return (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              data-testid={n.testid}
              className={`flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
                active ? "bg-[#09090b] text-white" : "text-zinc-700 hover:bg-zinc-100"
              }`}
            >
              <Icon className="w-4 h-4" />
              {n.i18n ? t(n.i18n) : n.label}
            </Link>
          );
        })}

        {visiblePortals.length > 0 && (
          <div className="mt-4 pt-3 border-t border-zinc-100">
            <button
              onClick={() => setPortalsOpen((v) => !v)}
              data-testid="nav-portals-toggle"
              className="w-full flex items-center justify-between px-3 py-2 overline text-zinc-500 hover:text-zinc-900"
            >
              <span>{t("side.portals")}</span>
              {portalsOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </button>
            {portalsOpen && (
              <div className="space-y-1">
                {visiblePortals.map((p) => {
                  const Icon = p.icon;
                  const active = isActive(p.to);
                  return (
                    <Link
                      key={p.to}
                      to={p.to}
                      onClick={() => setOpen(false)}
                      data-testid={p.testid}
                      className={`flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors ${
                        active ? "bg-[#09090b] text-white" : "text-zinc-700 hover:bg-zinc-100"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {p.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {user?.is_admin && (
          <Link
            to="/admin"
            onClick={() => setOpen(false)}
            data-testid="nav-admin"
            className={`mt-3 flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive("/admin") ? "bg-[#09090b] text-white" : "text-zinc-700 hover:bg-zinc-100"
            }`}
          >
            <Shield className="w-4 h-4" />
            {t("side.admin")}
          </Link>
        )}
      </nav>

      <div className="border-t border-zinc-200 p-3 space-y-1">
        <Link
          to="/profile"
          onClick={() => setOpen(false)}
          data-testid="nav-profile"
          className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-100"
        >
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-6 h-6 object-cover" />
          ) : (
            <UserCircle2 className="w-4 h-4" />
          )}
          <span className="truncate">{user?.name || user?.email}</span>
        </Link>
        <button
          onClick={handleLogout}
          data-testid="nav-logout"
          className="w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium text-zinc-700 hover:bg-zinc-100"
        >
          <LogOut className="w-4 h-4" />
          {t("side.logout")}
        </button>
      </div>
    </>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [portalsOpen, setPortalsOpen] = useState(true);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const isActive = (to) => location.pathname === to;

  return (
    <div className="min-h-screen bg-white">
      {/* Mobile top bar */}
      <div className="lg:hidden flex items-center justify-between border-b border-zinc-200 px-4 py-3 sticky top-0 bg-white z-30">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img src="/brand/miresco-logo.png" alt="MIRESCO" className="w-8 h-8 object-contain" />
          <span className="font-display font-black tracking-tight">MIRESCO BIZ</span>
        </Link>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <NotificationBell data-testid="notification-bell-mobile" />
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-open" className="p-2 border border-zinc-200">
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div className="w-72 bg-white border-r border-zinc-200 h-full flex flex-col">
            <div className="flex justify-end p-3 border-b border-zinc-200">
              <button onClick={() => setOpen(false)} data-testid="mobile-menu-close" className="p-2 border border-zinc-200">
                <X className="w-5 h-5" />
              </button>
            </div>
            <SidebarBody user={user} isActive={isActive} setOpen={setOpen} handleLogout={handleLogout} portalsOpen={portalsOpen} setPortalsOpen={setPortalsOpen} />
          </div>
          <div className="flex-1 bg-black/30" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex">
        <aside className="hidden lg:flex w-64 flex-col border-r border-zinc-200 h-screen sticky top-0">
          <SidebarBody user={user} isActive={isActive} setOpen={setOpen} handleLogout={handleLogout} portalsOpen={portalsOpen} setPortalsOpen={setPortalsOpen} />
        </aside>

        <main className="flex-1 min-w-0 p-4 sm:p-6 lg:p-10">
          <div className="hidden lg:flex items-center justify-end gap-3 mb-6 -mt-2">
            <LanguageSwitcher />
            <NotificationBell data-testid="notification-bell" />
          </div>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
